import React, { useEffect, useState } from "react";
import { getLeaderboard } from "../../lib/api";

const LAUNCH = new Date("2027-02-07T00:00:00+11:00"); // Melbourne daylight time

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, done: diff === 0 };
}

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="font-serif font-light text-bone text-4xl md:text-6xl leading-none tabular-nums">{String(value).padStart(2, "0")}</span>
    <span className="mt-2 text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-bone/40">{label}</span>
  </div>
);

export default function LiveLeaderboard() {
  const [rows, setRows] = useState([]);
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH);

  useEffect(() => {
    let mounted = true;
    getLeaderboard().then((d) => mounted && setRows(Array.isArray(d) ? d : [])).catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <section className="bg-ink text-bone px-6 md:px-16 py-24 md:py-32" data-testid="live-leaderboard">
      {/* Countdown banner */}
      <div className="max-w-5xl mx-auto text-center" data-testid="launch-countdown">
        <p className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-mocha" data-testid="launch-countdown-eyebrow">Melbourne, we’re coming</p>
        <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98]">
          07.02.27
        </h2>
        <p className="mt-4 text-sm text-bone/60" data-testid="early-access-date">Early access opens 7 January 2027.</p>
        <div className="mt-10 flex items-start justify-center gap-6 md:gap-12">
          <Unit value={days} label="Days" />
          <span className="font-serif font-light text-bone/25 text-4xl md:text-6xl leading-none">:</span>
          <Unit value={hours} label="Hours" />
          <span className="font-serif font-light text-bone/25 text-4xl md:text-6xl leading-none">:</span>
          <Unit value={minutes} label="Minutes" />
          <span className="font-serif font-light text-bone/25 text-4xl md:text-6xl leading-none">:</span>
          <Unit value={seconds} label="Seconds" />
        </div>
      </div>

      {rows.length > 0 && (
        <div className="max-w-3xl mx-auto mt-24 md:mt-28" data-testid="live-leaderboard-venues">
          <div className="flex items-baseline justify-between border-b border-bone/15 pb-5">
            <h3 className="font-serif font-light text-2xl md:text-3xl" data-testid="leaderboard-heading">Early-access favourites</h3>
            <span className="text-[10px] tracking-[0.28em] uppercase text-bone/40" data-testid="leaderboard-live-interest">Preview interest</span>
          </div>
          <ol data-testid="leaderboard-list">
            {rows.map((r) => (
              <li
                key={r.salon_slug}
                className="group flex items-center gap-6 md:gap-10 border-b border-bone/10 py-6 md:py-7 transition-colors hover:bg-bone/[0.03]"
                data-testid={`leaderboard-row-${r.rank}`}
              >
                <span className="font-serif font-light text-mocha/70 text-2xl md:text-3xl w-10 shrink-0 tabular-nums">{String(r.rank).padStart(2, "0")}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-xl md:text-2xl text-bone truncate" data-testid={`leaderboard-name-${r.rank}`}>{r.salon_name}</p>
                  {r.suburb && <p className="mt-1 text-[10px] tracking-[0.26em] uppercase text-bone/40">{r.suburb}</p>}
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-serif font-light text-2xl md:text-3xl text-bone tabular-nums">{r.bookings > 0 ? r.bookings : r.openings}</p>
                  <p className="mt-1 text-[9px] tracking-[0.24em] uppercase text-bone/40">{r.bookings > 0 ? "interest" : "preview"}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
